/**
 * Utility functions for Laporan Rekap Kohort Lengkap.
 */

export const MonthOptions = [
  { label: "Januari", value: 1 },
  { label: "Februari", value: 2 },
  { label: "Maret", value: 3 },
  { label: "April", value: 4 },
  { label: "Mei", value: 5 },
  { label: "Juni", value: 6 },
  { label: "Juli", value: 7 },
  { label: "Agustus", value: 8 },
  { label: "September", value: 9 },
  { label: "Oktober", value: 10 },
  { label: "November", value: 11 },
  { label: "Desember", value: 12 },
];

/**
 * Build a Map assigning sequential codes (TP-1, TP-2, ...) to unique tp_id
 * based on order of appearance in the rows.
 *
 * @param {Array<Object>} rows
 * @returns {Map<any, string>}
 */
export const buildTpCodeMap = (rows = []) => {
  const map = new Map();
  if (!Array.isArray(rows)) return map;

  let count = 0;
  rows.forEach((row) => {
    const tpId = row?.tp_id;
    if (tpId != null && !map.has(tpId)) {
      count += 1;
      map.set(tpId, `TP-${count}`);
    }
  });

  return map;
};

/**
 * Calculate density ratio (total_larva / total_catch * 100) with 2 decimal places.
 * Returns "-" if total_catch <= 0 or values are not valid numbers.
 *
 * @param {Object} row
 * @returns {string}
 */
export const getDensity = (row = {}) => {
  const catchVal = parseFloat(row?.total_catch);
  const larvaVal = parseFloat(row?.total_larva);

  if (!isNaN(catchVal) && !isNaN(larvaVal) && catchVal > 0) {
    return ((larvaVal / catchVal) * 100).toFixed(2);
  }
  return "-";
};

/**
 * Format larva phase list into comma-separated string or fallback to "-".
 *
 * @param {any} phase
 * @returns {string}
 */
export const formatLarvaPhase = (phase) => {
  if (Array.isArray(phase)) {
    return phase.length > 0 ? phase.join(", ") : "-";
  }
  return phase || "-";
};

/**
 * Check if a specific intervention type exists in intervention_done array.
 *
 * @param {any} interventions
 * @param {string} type
 * @returns {"Ya" | "Tidak"}
 */
export const hasInterventionType = (interventions, type) => {
  if (!Array.isArray(interventions)) return "Tidak";
  return interventions.includes(type) ? "Ya" : "Tidak";
};

/**
 * Extract other intervention text starting with "Lainnya_".
 *
 * @param {any} interventions
 * @returns {string}
 */
export const getInterventionOthers = (interventions) => {
  if (!Array.isArray(interventions)) return "-";
  const found = interventions.find(
    (x) => typeof x === "string" && x.startsWith("Lainnya_")
  );
  return found ? found.replace("Lainnya_", "") : "-";
};

/**
 * Check whether "Belum Intervensi" applies.
 *
 * @param {any} interventions
 * @returns {"Ya" | "Tidak"}
 */
export const isBelumIntervensi = (interventions) => {
  if (!Array.isArray(interventions)) return "Tidak";
  return interventions.some(
    (x) =>
      typeof x === "string" &&
      (x.startsWith("Belum") || x === "Belum dilakukan Intervensi")
  )
    ? "Ya"
    : "Tidak";
};

/**
 * Extract planned intervention text from items starting with "Belum_".
 *
 * @param {any} interventions
 * @returns {string}
 */
export const getInterventionBelum = (interventions) => {
  if (!Array.isArray(interventions)) return "-";
  const found = interventions.find(
    (x) =>
      typeof x === "string" &&
      (x.startsWith("Belum_") || x === "Belum dilakukan Intervensi")
  );
  if (!found) return "-";
  return found.startsWith("Belum_")
    ? found.replace("Belum_", "")
    : "Belum dilakukan Intervensi";
};

/**
 * Parse comma-separated fasyankes multi-select string into array of trimmed fasyankes codes.
 *
 * @param {string|Array<string>} selectedFasyankes
 * @returns {Array<string>}
 */
export const parseFasyankesCodes = (selectedFasyankes) => {
  if (!selectedFasyankes) return [];
  const raw = Array.isArray(selectedFasyankes)
    ? selectedFasyankes.join(",")
    : String(selectedFasyankes);

  return raw
    .split(",")
    .map((item) => item.split("|")[0].trim())
    .filter(Boolean);
};

/**
 * Filter populations by list of fasyankes codes and matching year.
 *
 * @param {Array<Object>} populations
 * @param {Array<string>} fasyankesCodes
 * @param {string|number} year
 * @returns {Array<Object>}
 */
export const filterPopulationsByFasyankes = (
  populations = [],
  fasyankesCodes = [],
  year = ""
) => {
  if (
    !Array.isArray(populations) ||
    !Array.isArray(fasyankesCodes) ||
    fasyankesCodes.length === 0
  ) {
    return [];
  }
  const codesSet = new Set(fasyankesCodes);
  const targetYear = year ? String(year).trim() : "";

  return populations.filter((p) => {
    const code = String(p?.fasyankes_id || "").split("|")[0].trim();
    const matchFaskes = codesSet.has(code);
    const matchYear =
      !targetYear || String(p?.year || "").trim() === targetYear;
    return matchFaskes && matchYear;
  });
};

/**
 * Filter larva identifications by list of fasyankes codes.
 *
 * @param {Array<Object>} identifications
 * @param {Array<string>} fasyankesCodes
 * @returns {Array<Object>}
 */
export const filterLarvaByFasyankes = (
  identifications = [],
  fasyankesCodes = []
) => {
  if (
    !Array.isArray(identifications) ||
    !Array.isArray(fasyankesCodes) ||
    fasyankesCodes.length === 0
  ) {
    return [];
  }
  const codesSet = new Set(fasyankesCodes);

  return identifications.filter((r) => {
    const code = String(r?.fasyankes_id || "").split("|")[0].trim();
    return codesSet.has(code);
  });
};

/**
 * Build distinct desa filter options from items with urbanvillage_id ("code|name").
 *
 * @param {Array<Object>} items
 * @returns {Array<{ label: string, value: string }>}
 */
export const getDesaFilterOptions = (items = []) => {
  if (!Array.isArray(items)) return [];

  const map = new Map();
  items.forEach((item) => {
    const raw = item?.urbanvillage_id;
    if (!raw) return;
    const parts = String(raw).split("|");
    const code = (parts[0] || "").trim();
    if (!code) return;
    const name = (parts[1] || "").trim() || code;
    if (!map.has(code)) {
      map.set(code, { label: name, value: code });
    }
  });

  return Array.from(map.values());
};

/**
 * Build distinct TP name filter options from larva identifications,
 * optionally narrowed to selectedDesa code.
 *
 * @param {Array<Object>} identifications
 * @param {string} selectedDesa
 * @returns {Array<{ label: string, value: string }>}
 */
export const getTpFilterOptions = (identifications = [], selectedDesa = "") => {
  if (!Array.isArray(identifications)) return [];

  const targetDesa = String(selectedDesa || "").trim();
  const set = new Set();

  identifications.forEach((r) => {
    const rawDesa = r?.urbanvillage_id;
    const desaCode = rawDesa ? String(rawDesa).split("|")[0].trim() : "";
    if (targetDesa && desaCode !== targetDesa) {
      return;
    }
    const name = (r?.name_breeding_place || "").trim();
    if (name) {
      set.add(name);
    }
  });

  return Array.from(set).map((name) => ({ label: name, value: name }));
};
